import { RelationItem, RelationSetType } from "./relationship.d";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Divider,
  Chip,
} from "@heroui/react";
import { ChevronDown, ChevronUp, Merge, Split, Trash2 } from "lucide-react";
import { LimeCollapse } from "../NextBase/LimeCollapse";
import RelationItemSelector from "./RelationItemSelector";
import { Trans } from "@lingui/react/macro";
import { cn } from "@/utils";

const RelationSetCard = ({
  relationSet,
  onChange,
  isExpanded,
  onToggleExpand,
  onDelete,
  items,
}: {
  relationSet: RelationSetType;
  onChange: (relationSet: RelationSetType) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onDelete: () => void;
  items: RelationItem[];
}) => {
  const handleGroupsChange = (items: RelationSetType["items"]) => {
    onChange({
      ...relationSet,
      items,
    });
  };

  return (
    <Card
      className={cn(
        "w-full outline-0 outline-primary transition-all duration-300",
        {
          "outline-2": isExpanded,
        },
      )}
      shadow="sm"
    >
      <CardHeader className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Chip
              color={relationSet.logicType === "AND" ? "primary" : "primary"}
            >
              <div className="flex select-none items-center gap-2">
                {relationSet.logicType === "AND" ? (
                  <>
                    <Merge size={16} />
                    <span>
                      <Trans>IN</Trans>
                    </span>
                  </>
                ) : (
                  <>
                    <Split size={16} />
                    <span>
                      <Trans>ALI</Trans>
                    </span>
                  </>
                )}
              </div>
            </Chip>
          </div>
          <p className="text-xs text-default-500">
            {relationSet.items.length} <Trans>izbranih</Trans>
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            isIconOnly
            variant="light"
            aria-label="Toggle expand"
            onPress={onToggleExpand}
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
          <Button
            isIconOnly
            variant="light"
            color="danger"
            aria-label="Delete resource set"
            onPress={onDelete}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </CardHeader>

      <LimeCollapse isOpen={isExpanded}>
        <Divider />
        <CardBody className="gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-small text-default-500">
              <Trans>Tip logike</Trans>:
            </p>
            <div className="flex items-center justify-between gap-2">
              <Button
                color={relationSet.logicType === "AND" ? "primary" : "default"}
                variant={relationSet.logicType === "AND" ? "solid" : "bordered"}
                onPress={() => onChange({ ...relationSet, logicType: "AND" })}
                startContent={<Merge className="shrink-0" size={16} />}
                className="w-full"
              >
                <Trans>IN (Vsa sredstva potrebna)</Trans>
              </Button>
              <Button
                color={relationSet.logicType === "OR" ? "primary" : "default"}
                variant={relationSet.logicType === "OR" ? "solid" : "bordered"}
                onPress={() => onChange({ ...relationSet, logicType: "OR" })}
                startContent={<Split className="shrink-0" size={16} />}
                className="w-full"
              >
                <Trans>ALI (Katero koli prosto sredstvo)</Trans>
              </Button>
            </div>
          </div>
          <RelationItemSelector
            items={items}
            selectedItems={relationSet.items}
            onChange={handleGroupsChange}
          />
        </CardBody>
        <CardFooter>
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              {relationSet.logicType === "AND" ? (
                <Merge size={16} />
              ) : (
                <Split size={16} />
              )}
              <span className="text-small text-default-600">
                {relationSet.logicType === "AND" ? (
                  <Trans>Vsa izbrana sredstva so potrebna</Trans>
                ) : (
                  <Trans>Potrebno je vsaj eno prosto sredstvo</Trans>
                )}
              </span>
            </div>
          </div>
        </CardFooter>
      </LimeCollapse>
    </Card>
  );
};

export default RelationSetCard;
